import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { discoverMappings } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { SyncMode } from "@shared/api-types";
import { SYNC_MODE_LABELS } from "@shared/utils";

interface DiscoveryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DiscoveryForm = ({ isOpen, onClose, onSuccess }: DiscoveryFormProps) => {
  const [selectedEntities, setSelectedEntities] = useState<string[]>(["pl_title", "pl_product"]);
  const [syncMode, setSyncMode] = useState<SyncMode>("ifEmpty");
  const [overwriteExisting, setOverwriteExisting] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: discoverMappings,
    onSuccess: (data) => {
      toast({
        title: "Discovery Complete",
        description: `Created ${data.fieldMappingsCreated} field mappings and ${data.choiceMappingsCreated} choice mappings`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to discover mappings",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (selectedEntities.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one entity",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    const request = {
      action: "discover" as const,
      entities: selectedEntities,
      syncMode,
    };

    mutation.mutate(request);
  };

  const toggleEntity = (entity: string) => {
    setSelectedEntities(prev => 
      prev.includes(entity) 
        ? prev.filter(e => e !== entity)
        : [...prev, entity]
    );
  };

  // Sample entities for demo purposes
  const availableEntities = ["pl_title", "pl_product", "pl_author", "pl_publisher"];

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium">Auto-Discover Mappings</DialogTitle>
        </DialogHeader>
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Automatically discover and create field mappings based on field name similarities between KUP and HS systems.
          </p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-1">
                Select Entities
              </Label>
              <Card className="border border-gray-300 rounded-md">
                <CardContent className="h-48 overflow-y-auto p-2 pt-6">
                  {availableEntities.map((entity) => (
                    <div key={entity} className="p-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id={`entity-${entity}`}
                          checked={selectedEntities.includes(entity)}
                          onCheckedChange={() => toggleEntity(entity)}
                        />
                        <Label 
                          htmlFor={`entity-${entity}`}
                          className="text-sm"
                        >
                          {entity}
                        </Label>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-1">
                Default Sync Mode
              </Label>
              <Select value={syncMode} onValueChange={(value) => setSyncMode(value as SyncMode)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select sync mode" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SYNC_MODE_LABELS) as SyncMode[]).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {SYNC_MODE_LABELS[mode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-sm text-gray-500">
                This will be applied to all discovered mappings.
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="overwrite" 
                checked={overwriteExisting}
                onCheckedChange={(checked) => setOverwriteExisting(!!checked)}
              />
              <Label 
                htmlFor="overwrite"
                className="text-sm text-gray-700"
              >
                Overwrite existing mappings if found
              </Label>
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#0078D4] hover:bg-[#2B88D8]"
              disabled={isLoading}
            >
              {isLoading ? "Discovering..." : "Discover Mappings"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DiscoveryForm;
